import React, { useCallback, useEffect } from "react"
import { Trans } from "@lingui/macro"
import { match, P } from "ts-pattern"
import { CheckoutFlowType } from "@ssc-booking/api-contracts"
import { Geolocation } from "../app-core/_common/providers/geolocation-provider"
import { useECommerceMachine, useNotificationMachine } from "../app-core/_common/machines/machines-context"
import {
    ErrorStateContext,
    NotificationState,
    NotificationStateContext,
} from "../app-core/_common/machines/notification-machine"
import ErrorBox from "../components/ErrorBox/ErrorBox"
import InfoBox from "../components/ErrorBox/InfoBox"
import NoAvailabilityBox from "../components/ErrorBox/NoAvailabilityBox"
import FatalBox from "../components/ErrorBox/FatalBox"
import CreateQuoteBox from "../components/ErrorBox/CreateQuoteBox"
import { ECommerceContext } from "../app-core/v3/_common/machines/ecommerce-machine/e-commerce-machine-context"
import { buildErrorDataLayer } from "../app-core/v3/_common/tracking/page-data-layer"
import { useSuiteCategoriesReadModel } from "../app-core/v3/_common/machines/suite-categories-machine"
import { useReceiptReadModel } from "../app-core/v3/_common/machines/receipt-machine"
import { isBrowser } from "../app-core/_common/rendering"

type Props = {
    cruiseCode: string
    geoLocation: Geolocation
}

const AppNotifications: React.FC<React.PropsWithChildren<Props>> = ({ cruiseCode }) => {
    const [state, nfSend] = useNotificationMachine()
    const [ecommerceState] = useECommerceMachine()
    const [stateSCRM] = useSuiteCategoriesReadModel(ecommerceState)
    const [stateRP] = useReceiptReadModel(ecommerceState)

    const ecommerceContext = ecommerceState.context
    const homePath = ecommerceState.context.flowVersion.router.homeUrl(cruiseCode)

    const onCloseNotification = useCallback(() => {
        nfSend({ type: "CLOSE" })
    }, [nfSend])

    const gotoHome = useCallback(() => {
        location.href = homePath
    }, [homePath])

    useEffect(() => {
        if (state.matches("closed")) return

        notifyRollbar(ecommerceContext, state)

        const activeStep = ecommerceContext.activeStep
        const step = ecommerceContext.flowVersion.tracking.step(activeStep)
        const errorData = buildErrorDataLayer(state.context, ecommerceState, stateSCRM, stateRP, step)
        window.wspr?.P("ecommerce_error", errorData)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.value])

    if (state.matches("notification")) {
        return match(state.context.kind)
            .with("noAvailability", () => (
                <NoAvailabilityBox
                    cruiseCode={ecommerceContext.cruiseCode}
                    destination={ecommerceContext.sanityContent.cruise.destination}
                    occupancy={ecommerceContext.guests}
                />
            ))
            .with("cannotReload", () => (
                <InfoBox
                    title={<Trans>Cannot reload page</Trans>}
                    message={
                        <Trans>
                            Unfortunately this page cannot be reloaded.
                            <br /> Please try a new configuration
                        </Trans>
                    }
                    button={{
                        label: <Trans>Try new configuration</Trans>,
                        onClick: gotoHome,
                    }}
                />
            ))
            .with("selectedSuiteNoMoreAvailable", () => (
                <InfoBox
                    title={<Trans>Suite not available</Trans>}
                    message={
                        <Trans>
                            Unfortunately the suite you requested has already been booked.
                            <br /> Please select another suite
                        </Trans>
                    }
                    button={{
                        label: <Trans>Choose another suite</Trans>,
                        onClick: gotoHome,
                    }}
                />
            ))
            .with("cannotSelectSuite", () => (
                <InfoBox
                    title={<Trans>Cannot select the requested suite</Trans>}
                    message={
                        <Trans>
                            There might be a problem with your selection.
                            <br /> Please refresh the page
                        </Trans>
                    }
                    button={{
                        label: <Trans>Choose another suite</Trans>,
                        onClick: gotoHome,
                    }}
                />
            ))
            .with("invalidCaptcha", () => (
                <InfoBox
                    title={<Trans>It looks like there&apos;s a problem</Trans>}
                    message={<Trans>Invalid Captcha. Please submit again.</Trans>}
                    button={{
                        label: <Trans>Close</Trans>,
                        onClick: onCloseNotification,
                    }}
                />
            ))
            .with("invalidParameter", () => (
                <InfoBox
                    title={<Trans>Cannot process your request</Trans>}
                    message={
                        <Trans>
                            It looks like there are issues with your personalization. Maybe you landed here using an
                            outdated link. Please try a new configuration.
                        </Trans>
                    }
                    button={{
                        label: <Trans>Try new configuration</Trans>,
                        onClick: gotoHome,
                    }}
                />
            ))
            .with("checkoutProcessExpired", () => {
                const info = state.context.info
                // TODO: should we type the info object?
                const flowType: CheckoutFlowType = (info as any)?.response?.flowType ?? ("ECommerce" as const)

                return match(flowType)
                    .with("ECommerce", () => (
                        <InfoBox
                            iconError={<Trans>Booking expired</Trans>}
                            title={<Trans>Your booking is no longer available</Trans>}
                            messageTitle={<Trans>Let’s find the next perfect suite</Trans>}
                            message={
                                <Trans>
                                    You can make another reservation right away, explore the available suite and secure
                                    your place onboard.
                                </Trans>
                            }
                            button={{
                                label: <Trans>Find another suite</Trans>,
                                onClick: gotoHome,
                            }}
                        />
                    ))
                    .with("CourtesyHold", () => (
                        <InfoBox
                            iconError={<Trans>Courtesy hold expired</Trans>}
                            title={<Trans>Your courtesy hold is no longer available</Trans>}
                            messageTitle={<Trans>Let’s find the next perfect suite</Trans>}
                            message={
                                <Trans>
                                    You can make another reservation right away, explore the available suite and secure
                                    your place onboard.
                                </Trans>
                            }
                            button={{
                                label: <Trans>Find another suite</Trans>,
                                onClick: gotoHome,
                            }}
                        />
                    ))
                    .exhaustive()
            })
            .exhaustive()
    }
    if (state.matches("error")) {
        return (
            match(state.context)
                // Retryable
                .with({ kind: "generic", errorCode: "connectionClosed" }, () => (
                    <ErrorBox
                        button={{
                            label: <Trans>Try again</Trans>,
                            onClick: gotoHome,
                        }}
                        message={<Trans>Your connection is unstable, please try your configuration again.</Trans>}
                    />
                ))
                // Retryable
                .with({ kind: "generic", errorCounter: P.union(P.nullish, 0, 1, 2) }, () => (
                    <ErrorBox
                        hideIcon
                        button={{
                            label: <Trans>Try again</Trans>,
                            onClick: gotoHome,
                        }}
                        title={<Trans>It looks like there&apos;s a problem</Trans>}
                        message={<Trans>We apologize, please try your configuration again.</Trans>}
                    />
                ))
                .with({ kind: "generic", errorCounter: P.number }, () => (
                    // TODO aggiungere RAQ
                    <ErrorBox
                        button={{
                            label: <Trans>Try again</Trans>,
                            onClick: gotoHome,
                        }}
                        message={<Trans>We apologize, please try your configuration again.</Trans>}
                    />
                ))
                // Not retryable
                .with({ kind: "fatal" }, () => (
                    <FatalBox buttonLabel={<Trans>Try again</Trans>} onButtonClick={gotoHome} />
                ))
                .with({ kind: "createQuote" }, () => (
                    <CreateQuoteBox buttonLabel={<Trans>Try again</Trans>} onButtonClick={gotoHome} />
                ))
                .exhaustive()
        )
    }
    return null
}

const notifyRollbar = (ecommerceContext: ECommerceContext, nfState: NotificationState) => {
    if (!isBrowser()) return
    if (nfState.matches("notification")) notifyLog(ecommerceContext, nfState.context)
    if (nfState.matches("error")) {
        const kind = nfState.context.kind
        const nfContext = nfState.context

        match(kind)
            .with("fatal", notifyError(ecommerceContext, nfContext))
            .with("generic", notifyWarning(ecommerceContext, nfContext))
            .with("createQuote", notifyWarning(ecommerceContext, nfContext))
            .exhaustive()
    }
}

const notifyError = (mainContext: unknown, nfContext: ErrorStateContext) => () => {
    const notification = toJSON(nfContext)
    const context = toJSON(mainContext)
    console.error(nfContext.message, notification)
    console.log("ERROR CONTEXT", context)
    window.rollbar?.error(nfContext.message, { notification, context })
}

const notifyWarning = (mainContext: unknown, nfContext: ErrorStateContext) => () => {
    const notification = toJSON(nfContext)
    const context = toJSON(mainContext)
    console.warn(nfContext.message, notification)
    console.log("WARNING CONTEXT", context)
    window.rollbar?.warning(nfContext.message, { notification, context })
}

const notifyLog = (mainContext: unknown, nfContext: NotificationStateContext) => () => {
    const notification = toJSON(nfContext)
    const context = toJSON(mainContext)
    const message = `AppNotifications: ${nfContext.kind}`
    console.log(message, nfContext)
    console.log("LOG CONTEXT", context)
    window.rollbar?.log(message, { notification, context })
}

const toJSON = (x: unknown) => JSON.parse(JSON.stringify(x))

export default AppNotifications
